/**
 * Dou Dizhu (斗地主) cards. Ids 0…51 are the 炸金花 ids, so the web reuses `art/cards.ts`:
 *   face = id >> 2 (0 = '2', 1 = '3' … 8 = '10', 9 = J, 10 = Q, 11 = K, 12 = A), suit = id & 3 (0 ♠, 1 ♥, 2 ♣, 3 ♦);
 *   52 = small joker (小王), 53 = big joker (大王).
 * Dou Dizhu rank (`ddzRank`, 0…14): 3 4 5 6 7 8 9 10 J Q K A 2 小王 大王. Suits never matter.
 */
export const DDZ_DECK = 54;
export const SMALL_JOKER = 52;
export const BIG_JOKER = 53;
export const DDZ_RANKS = 15;
export const RANK_A = 11;
export const RANK_2 = 12;
export const RANK_SJ = 13;
export const RANK_BJ = 14;
/** rank → short name (tests, logs): T = 10, SJ / BJ = jokers */
export const RANK_NAMES: readonly string[] = [
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'T',
  'J',
  'Q',
  'K',
  'A',
  '2',
  'SJ',
  'BJ',
];

const FACES = '23456789TJQKA';
const SUITS = 'shcd';

function checkCard(card: number): void {
  if (!Number.isInteger(card) || card < 0 || card >= DDZ_DECK) throw new RangeError(`bad card ${card}`);
}

export function ddzRank(card: number): number {
  checkCard(card);
  if (card >= SMALL_JOKER) return card === SMALL_JOKER ? RANK_SJ : RANK_BJ;
  const face = card >> 2;
  return face === 0 ? RANK_2 : face - 1;
}

/** "As" / "Th" / "SJ" / "BJ" → id */
export function ddzCard(name: string): number {
  if (name === 'SJ') return SMALL_JOKER;
  if (name === 'BJ') return BIG_JOKER;
  const face = FACES.indexOf(name.charAt(0));
  const suit = SUITS.indexOf(name.charAt(1));
  if (name.length !== 2 || face < 0 || suit < 0) throw new RangeError(`bad card "${name}"`);
  return face * 4 + suit;
}

/** "3s 3h SJ" → ids */
export function ddzCards(names: string): number[] {
  return names
    .split(/\s+/)
    .filter((x) => x !== '')
    .map(ddzCard);
}

export function ddzCardName(card: number): string {
  checkCard(card);
  if (card === SMALL_JOKER) return 'SJ';
  if (card === BIG_JOKER) return 'BJ';
  return `${FACES.charAt(card >> 2)}${SUITS.charAt(card & 3)}`;
}

/** ascending by Dou Dizhu rank, then by id — the canonical order of hands and patterns */
export function ddzSort(cards: readonly number[]): number[] {
  return [...cards].sort((a, b) => ddzRank(a) - ddzRank(b) || a - b);
}

/** cards per rank, index = ddzRank */
export function rankCounts(cards: readonly number[]): number[] {
  const c = new Array<number>(DDZ_RANKS).fill(0);
  for (const card of cards) {
    const r = ddzRank(card);
    c[r] = (c[r] as number) + 1;
  }
  return c;
}
